import { useState, useEffect, useMemo } from 'react';
import {
  google,
  outlook,
  office365,
  yahoo,
  ics,
  CalendarEvent
} from 'calendar-link';
import { useAppSelectorShallow } from 'store/hooks';
import {
  calculateSequenceDuration,
  determineRoutineNextStartTime
} from 'utils/helpers';
import { saveAs } from 'file-saver';
import { t } from 'services/i18n.service';
import {
  CALENDAR_OPTIONS,
  CALENDAR_RECURRENCE_RULE_FOR_DAILY_RECURRENCE,
  UTC_TIMEZONE
} from 'constants/general';
import { ActivityTableFilter, CalendarTypes } from 'enums';
import { ATTRIB } from 'constants/test';
import UiSelect from 'components/ui/Select';

export default function RoutineCalendarReminders() {
  const {
    setting: {
      currentSettings: {
        morning_activities,
        evening_activities,
        startup_time,
        shutdown_time
      }
    },
    user: {
      details: { timezone }
    }
  } = useAppSelectorShallow((state) => state);
  const timeZoneToUse = timezone ?? UTC_TIMEZONE;
  const [calendarToUse, setCalendarToUse] = useState<string | undefined>(
    'google'
  );
  const [morningRoutineEventLink, setMorningRoutineEventLink] = useState('');
  const [eveningRoutineEventLink, setEveningRoutineEventLink] = useState('');
  const morningSequenceDuration = calculateSequenceDuration(
    morning_activities ?? []
  );
  const eveningSequenceDuration = calculateSequenceDuration(
    evening_activities ?? []
  );
  const morningRoutineStart = determineRoutineNextStartTime(
    timeZoneToUse,
    startup_time
  );
  const eveningRoutineStart = determineRoutineNextStartTime(
    timeZoneToUse,
    shutdown_time
  );

  const morningEvent: CalendarEvent = useMemo(
    () => ({
      title: t('inquiry.do_morning_routine'),
      start: morningRoutineStart,
      duration: [morningSequenceDuration, 'seconds'],
      rRule: CALENDAR_RECURRENCE_RULE_FOR_DAILY_RECURRENCE
    }),
    [morningRoutineStart, morningSequenceDuration]
  );

  const eveningEvent: CalendarEvent = useMemo(
    () => ({
      title: t('inquiry.do_evening_routine'),
      start: eveningRoutineStart,
      duration: [eveningSequenceDuration, 'seconds'],
      rRule: CALENDAR_RECURRENCE_RULE_FOR_DAILY_RECURRENCE
    }),
    [eveningRoutineStart, eveningSequenceDuration]
  );

  useEffect(() => {
    switch (calendarToUse) {
      case CalendarTypes.OUTLOOK:
        setMorningRoutineEventLink(outlook(morningEvent));
        setEveningRoutineEventLink(outlook(eveningEvent));
        break;
      case CalendarTypes.YAHOO:
        setMorningRoutineEventLink(yahoo(morningEvent));
        setEveningRoutineEventLink(yahoo(eveningEvent));
        break;
      case CalendarTypes.MICROSOFT_CALENDAR:
        setMorningRoutineEventLink(office365(morningEvent));
        setEveningRoutineEventLink(office365(eveningEvent));
        break;
      case CalendarTypes.ICS:
        setMorningRoutineEventLink(ics(morningEvent));
        setEveningRoutineEventLink(ics(eveningEvent));
        break;
      default:
        setMorningRoutineEventLink(google(morningEvent));
        setEveningRoutineEventLink(google(eveningEvent));
    }
  }, [calendarToUse, eveningEvent, morningEvent]);

  const handleSaveIcsFile = (file: string, routine: string) => {
    const blob = new Blob([file], { type: 'text/plain;charset=utf-8' });
    saveAs(blob, `${routine}-routine-reminder.ics`);
  };

  return (
    <>
      <UiSelect
        className='w-full text-sm font-semibold'
        options={CALENDAR_OPTIONS}
        value={calendarToUse}
        onChange={(value) => {
          if (!value) return;
          setCalendarToUse(value);
        }}
        size='sm'
      />
      {calendarToUse !== CalendarTypes.ICS && (
        <div className='flex md:grid grid-cols-2 flex-col justify-start gap-2'>
          <a href={morningRoutineEventLink} target='_blank' rel='noreferrer'>
            <button
              className='md:w-fit w-full h-fit p-4 py-1 bg-gray-500 hover:bg-gray-600 text-white rounded text-xs lg:text-sm'
              data-testid={
                ATTRIB.DATA_TESTID.MORNING_ROUTINE_ROUTINE_REMINDER_BTN
              }>
              {t('inquiry.set_morning_routine_reminder')}
            </button>
          </a>
          <a href={eveningRoutineEventLink} target='_blank' rel='noreferrer'>
            <button
              className='md:w-fit w-full xs:w-full h-fit p-4 py-1 bg-gray-500 hover:bg-gray-600 text-white rounded text-xs lg:text-sm'
              data-testid={
                ATTRIB.DATA_TESTID.EVENING_ROUTINE_ROUTINE_REMINDER_BTN
              }>
              {t('inquiry.set_evening_routine_reminder')}
            </button>
          </a>
        </div>
      )}
      {calendarToUse === CalendarTypes.ICS && (
        <div className='flex md:grid grid-cols-2 flex-col justify-start gap-2'>
          <button
            onClick={() =>
              handleSaveIcsFile(
                morningRoutineEventLink,
                ActivityTableFilter.MORNING
              )
            }
            className='md:w-fit w-full h-fit p-4 py-1 bg-gray-500 hover:bg-gray-600 text-white rounded text-xs lg:text-sm'>
            {t('inquiry.set_morning_routine_reminder')}
          </button>
          <button
            onClick={() =>
              handleSaveIcsFile(
                eveningRoutineEventLink,
                ActivityTableFilter.EVENING
              )
            }
            className='md:w-fit w-full h-fit p-4 py-1 bg-gray-500 hover:bg-gray-600 text-white rounded text-xs lg:text-sm'>
            {t('inquiry.set_evening_routine_reminder')}
          </button>
        </div>
      )}
    </>
  );
}
